import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import {
  Users,
  UserPlus,
  Mail,
  Shield,
  Eye,
  Edit,
  Trash2,
  MoreVertical,
  Loader2,
  RefreshCw,
  Check,
  X,
  Clock,
  FolderKanban
} from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import { employeeService, Employee, EmployeePermissions, getDefaultPermissions } from '../../services/employeeService';
import { useAuth } from '../../contexts/AuthContext';
import { useProject } from '../../contexts/ProjectContext';
import UsageBadge from '../../components/ui/UsageBadge';

const EmployeesSettings: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { projects } = useProject();
  const [isLoading, setIsLoading] = useState(true);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState<Employee | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [inviteData, setInviteData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    password: '',
    role: 'viewer' as 'admin' | 'editor' | 'viewer',
    project_ids: [] as string[]
  });

  const [editData, setEditData] = useState({
    role: 'viewer' as 'admin' | 'editor' | 'viewer',
    status: 'active' as 'pending' | 'active' | 'suspended',
    project_ids: [] as string[],
    newPassword: ''
  });

  useEffect(() => {
    loadEmployees();
  }, []);

  const loadEmployees = async () => {
    setIsLoading(true);
    try {
      const response = await employeeService.getEmployees();
      if (response.success && response.data) {
        setEmployees(response.data);
      }
    } catch (error) {
      console.error('Error loading employees:', error);
      toast.error(t('employees.fetch_error'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleInvite = async () => {
    if (!inviteData.email || !inviteData.first_name || !inviteData.password) {
      toast.error(t('employees.fill_required_fields', 'Fill in all required fields'));
      return;
    }
    if (inviteData.password.length < 6) {
      toast.error(t('employees.password_min_length', 'Password must be at least 6 characters'));
      return;
    }
    if (!inviteData.project_ids || inviteData.project_ids.length === 0) {
      toast.error(t('employees.select_project', 'Select at least one project'));
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await employeeService.createEmployee({
        first_name: inviteData.first_name,
        last_name: inviteData.last_name,
        email: inviteData.email,
        password: inviteData.password,
        role: inviteData.role,
        project_ids: inviteData.project_ids
      });
      if (response.success) {
        toast.success(t('employees.created_success', 'Team member created successfully'));
        setShowInviteModal(false);
        setInviteData({ first_name: '', last_name: '', email: '', password: '', role: 'viewer', project_ids: [] });
        loadEmployees();
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || t('employees.create_error', 'Error creating team member'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = (employee: Employee) => {
    setSelectedEmployee(employee);
    setEditData({
      role: employee.role,
      status: employee.status,
      project_ids: employee.projects?.map(p => p.id) || [],
      newPassword: ''
    });
    setShowEditModal(true);
  };

  const handleSaveEdit = async () => {
    if (!selectedEmployee) return;

    setIsSubmitting(true);
    try {
      const response = await employeeService.updateEmployee(selectedEmployee.id, {
        role: editData.role,
        status: editData.status,
        project_ids: editData.project_ids
      });

      // Change password if provided
      if (editData.newPassword && editData.newPassword.length >= 6) {
        await employeeService.changeEmployeePassword(selectedEmployee.id, { password: editData.newPassword });
      }

      if (response.success) {
        toast.success(t('employees.update_success'));
        setShowEditModal(false);
        setSelectedEmployee(null);
        loadEmployees();
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || t('employees.update_error'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRemove = async (employeeId: string) => {
    if (!confirm(t('employees.confirm_remove'))) return;

    try {
      const response = await employeeService.removeEmployee(employeeId);
      if (response.success) {
        toast.success(t('employees.remove_success'));
        loadEmployees();
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || t('employees.remove_error'));
    }
  };

  const handleResendInvite = async (employeeId: string) => {
    try {
      const response = await employeeService.resendInvitation(employeeId);
      if (response.success) {
        toast.success(t('employees.invite_resent'));
        loadEmployees(); // Reload the list
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || t('employees.resend_error'));
    }
  };

  const getRoleBadgeColor = (role: string) => {
    switch (role) {
      case 'admin':
        return 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400';
      case 'editor':
        return 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400';
      case 'viewer':
      default:
        return 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300';
    }
  };

  const getStatusBadgeColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400';
      case 'pending':
        return 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400';
      case 'suspended':
        return 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400';
      default:
        return 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300';
    }
  };

  const getRoleLabel = (role: string) => {
    switch (role) {
      case 'admin':
        return t('employees.role_admin');
      case 'editor':
        return t('employees.role_editor');
      case 'viewer':
      default:
        return t('employees.role_viewer');
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'active':
        return t('employees.status_active');
      case 'pending':
        return t('employees.status_pending');
      case 'suspended':
        return t('employees.status_suspended');
      default:
        return status;
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    );
  }

  return (
    <div className="space-y-3 sm:space-y-6">
      {/* Header */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Users className="w-5 h-5" />
              {t('employees.team_members')}
            </CardTitle>
            <div className="flex items-center gap-3">
              <UsageBadge resource="team_members" />
              <Button
                variant="primary"
                onClick={() => setShowInviteModal(true)}
                leftIcon={<UserPlus className="w-4 h-4" />}
              >
                {t('employees.add_member', 'Add Member')}
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {/* Owner (current user) */}
          <div className="flex items-center justify-between p-4 bg-primary-50 dark:bg-primary-900/20 rounded-xl border border-primary-200 dark:border-primary-800 mb-4">
            <div className="flex items-center gap-4">
              {user?.avatar_url ? (
                <img
                  src={user.avatar_url}
                  alt={user.first_name}
                  className="w-12 h-12 rounded-full object-cover"
                />
              ) : (
                <div className="w-12 h-12 rounded-full bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center">
                  <span className="text-lg font-semibold text-primary-600 dark:text-primary-400">
                    {(user?.first_name || 'U').charAt(0).toUpperCase()}
                  </span>
                </div>
              )}
              <div>
                <h4 className="font-medium text-gray-900 dark:text-white">
                  {user?.first_name} {user?.last_name}
                </h4>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {user?.email}
                </p>
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-xs px-2 py-0.5 rounded-full bg-primary-100 dark:bg-primary-900/50 text-primary-700 dark:text-primary-300">
                    {t('employees.owner')}
                  </span>
                </div>
              </div>
            </div>
            <span className="text-sm text-gray-500 dark:text-gray-400">{t('employees.you')}</span>
          </div>

          {employees.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-500 dark:text-gray-400">
                {t('employees.no_other_members')}
              </p>
              <Button
                variant="secondary"
                className="mt-4"
                onClick={() => setShowInviteModal(true)}
                leftIcon={<UserPlus className="w-4 h-4" />}
              >
                {t('employees.invite_first')}
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              {employees.map((employee) => (
                <div
                  key={employee.id}
                  className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl border border-gray-200 dark:border-gray-600"
                >
                  <div className="flex items-center gap-4">
                    {employee.employee?.avatar_url ? (
                      <img
                        src={employee.employee.avatar_url}
                        alt={employee.employee.first_name}
                        className="w-12 h-12 rounded-full object-cover"
                      />
                    ) : (
                      <div className="w-12 h-12 rounded-full bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center">
                        <span className="text-lg font-semibold text-primary-600 dark:text-primary-400">
                          {(employee.employee?.first_name || employee.email).charAt(0).toUpperCase()}
                        </span>
                      </div>
                    )}
                    <div>
                      <h4 className="font-medium text-gray-900 dark:text-white">
                        {employee.employee
                          ? `${employee.employee.first_name} ${employee.employee.last_name}`
                          : employee.email}
                      </h4>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {employee.email}
                      </p>
                      <div className="flex flex-wrap items-center gap-2 mt-1">
                        <span className={`text-xs px-2 py-0.5 rounded-full ${getRoleBadgeColor(employee.role)}`}>
                          {getRoleLabel(employee.role)}
                        </span>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${getStatusBadgeColor(employee.status)}`}>
                          {getStatusLabel(employee.status)}
                        </span>
                        {employee.status === 'pending' && employee.invited_at && (
                          <span className="text-xs text-gray-400 flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {t('employees.invited_on')} {new Date(employee.invited_at).toLocaleDateString()}
                          </span>
                        )}
                      </div>
                      {employee.projects && employee.projects.length > 0 && (
                        <div className="flex flex-wrap items-center gap-1 mt-2">
                          <FolderKanban className="w-3 h-3 text-gray-400" />
                          {employee.projects.map((project) => (
                            <span
                              key={project.id}
                              className="text-xs px-2 py-0.5 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 flex items-center gap-1"
                            >
                              <span
                                className="w-2 h-2 rounded-full"
                                style={{ backgroundColor: project.color || '#6366f1' }}
                              />
                              {project.name}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleEdit(employee)}
                      title={t('common.edit')}
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRemove(employee.id)}
                      title={t('common.remove')}
                      className="text-red-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Role Descriptions */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="w-5 h-5" />
            {t('employees.roles_permissions')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {(['admin', 'editor', 'viewer'] as const).map((role) => (
              <div key={role} className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl">
                <div className="flex items-center gap-2 mb-2">
                  <span className={`px-2 py-1 rounded-full text-sm font-medium ${getRoleBadgeColor(role)}`}>
                    {getRoleLabel(role)}
                  </span>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {t(`employees.role_${role}_desc`)}
                </p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Invite Modal */}
      {showInviteModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl max-w-md w-full mx-4">
            <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                {t('employees.add_team_member', 'Add Team Member')}
              </h3>
              <button
                onClick={() => setShowInviteModal(false)}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            <div className="p-4 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('auth.firstName', 'First Name')} *
                  </label>
                  <Input
                    value={inviteData.first_name}
                    onChange={(e) => setInviteData({ ...inviteData, first_name: e.target.value })}
                    placeholder={t('auth.firstName', 'First Name')}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('auth.lastName', 'Last Name')}
                  </label>
                  <Input
                    value={inviteData.last_name}
                    onChange={(e) => setInviteData({ ...inviteData, last_name: e.target.value })}
                    placeholder={t('auth.lastName', 'Last Name')}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('employees.email')} *
                </label>
                <Input
                  type="email"
                  value={inviteData.email}
                  onChange={(e) => setInviteData({ ...inviteData, email: e.target.value })}
                  placeholder={t('employees.email_placeholder')}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('auth.password', 'Password')} *
                </label>
                <Input
                  type="password"
                  value={inviteData.password}
                  onChange={(e) => setInviteData({ ...inviteData, password: e.target.value })}
                  placeholder={t('employees.password_placeholder', 'Min. 6 characters')}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('employees.role')}
                </label>
                <Select
                  options={[
                    { value: 'viewer', label: t('employees.role_viewer') },
                    { value: 'editor', label: t('employees.role_editor') },
                    { value: 'admin', label: t('employees.role_admin') }
                  ]}
                  value={inviteData.role}
                  onChange={(e) => setInviteData({ ...inviteData, role: e.target.value as any })}
                />
              </div>

              {projects.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    <div className="flex items-center gap-2">
                      <FolderKanban className="w-4 h-4" />
                      {t('employees.projects')}
                    </div>
                  </label>
                  <div className="space-y-2 max-h-40 overflow-y-auto p-2 border border-gray-200 dark:border-gray-700 rounded-lg">
                    {projects.filter(p => p.is_active).map((project) => (
                      <label key={project.id} className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={inviteData.project_ids.includes(project.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setInviteData({ ...inviteData, project_ids: [...inviteData.project_ids, project.id] });
                            } else {
                              setInviteData({ ...inviteData, project_ids: inviteData.project_ids.filter(id => id !== project.id) });
                            }
                          }}
                          className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                        />
                        <span
                          className="w-3 h-3 rounded-full"
                          style={{ backgroundColor: project.color || '#6366f1' }}
                        />
                        <span className="text-sm text-gray-700 dark:text-gray-300">{project.name}</span>
                      </label>
                    ))}
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{t('employees.projects_hint')}</p>
                </div>
              )}
            </div>

            <div className="flex items-center justify-end gap-3 p-4 border-t border-gray-200 dark:border-gray-700">
              <Button variant="ghost" onClick={() => setShowInviteModal(false)}>
                {t('common.cancel')}
              </Button>
              <Button
                variant="primary"
                onClick={handleInvite}
                isLoading={isSubmitting}
                leftIcon={<UserPlus className="w-4 h-4" />}
              >
                {t('employees.create_member', 'Create Member')}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && selectedEmployee && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl max-w-md w-full mx-4">
            <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                {t('employees.edit_team_member')}
              </h3>
              <button
                onClick={() => setShowEditModal(false)}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            <div className="p-4 space-y-4">
              <div className="flex items-center gap-4 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl">
                <div className="w-12 h-12 rounded-full bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center">
                  <span className="text-lg font-semibold text-primary-600 dark:text-primary-400">
                    {(selectedEmployee.employee?.first_name || selectedEmployee.email).charAt(0).toUpperCase()}
                  </span>
                </div>
                <div>
                  <h4 className="font-medium text-gray-900 dark:text-white">
                    {selectedEmployee.employee
                      ? `${selectedEmployee.employee.first_name} ${selectedEmployee.employee.last_name}`
                      : selectedEmployee.email}
                  </h4>
                  <p className="text-sm text-gray-500 dark:text-gray-400">{selectedEmployee.email}</p>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('employees.role')}
                </label>
                <Select
                  options={[
                    { value: 'viewer', label: t('employees.role_viewer') },
                    { value: 'editor', label: t('employees.role_editor') },
                    { value: 'admin', label: t('employees.role_admin') }
                  ]}
                  value={editData.role}
                  onChange={(e) => setEditData({ ...editData, role: e.target.value as any })}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('employees.status')}
                </label>
                <Select
                  options={[
                    { value: 'active', label: t('employees.status_active') },
                    { value: 'suspended', label: t('employees.status_suspended') }
                  ]}
                  value={editData.status}
                  onChange={(e) => setEditData({ ...editData, status: e.target.value as any })}
                />
              </div>

              {projects.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    <div className="flex items-center gap-2">
                      <FolderKanban className="w-4 h-4" />
                      {t('employees.projects')}
                    </div>
                  </label>
                  <div className="space-y-2 max-h-40 overflow-y-auto p-2 border border-gray-200 dark:border-gray-700 rounded-lg">
                    {projects.filter(p => p.is_active).map((project) => (
                      <label key={project.id} className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={editData.project_ids.includes(project.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setEditData({ ...editData, project_ids: [...editData.project_ids, project.id] });
                            } else {
                              setEditData({ ...editData, project_ids: editData.project_ids.filter(id => id !== project.id) });
                            }
                          }}
                          className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                        />
                        <span
                          className="w-3 h-3 rounded-full"
                          style={{ backgroundColor: project.color || '#6366f1' }}
                        />
                        <span className="text-sm text-gray-700 dark:text-gray-300">{project.name}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="p-4 pt-0">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('employees.change_password', 'Change Password')}
                </label>
                <Input
                  type="password"
                  value={editData.newPassword}
                  onChange={(e) => setEditData({ ...editData, newPassword: e.target.value })}
                  placeholder={t('employees.new_password_placeholder', 'Leave empty to keep current')}
                />
                <p className="text-xs text-gray-400 mt-1">{t('employees.password_hint', 'Min. 6 characters. Leave empty to keep current password.')}</p>
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 p-4 border-t border-gray-200 dark:border-gray-700">
              <Button variant="ghost" onClick={() => setShowEditModal(false)}>
                {t('common.cancel')}
              </Button>
              <Button
                variant="primary"
                onClick={handleSaveEdit}
                isLoading={isSubmitting}
              >
                {t('common.save')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmployeesSettings;
